import React, { useState } from 'react'

import {
  AlignItems,
  Body1,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Select,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { useEditDeliveryDay } from '../useEditDeliveryDay'

// Citrus Select's types omit `value` (HTMLAttributes vs SelectHTMLAttributes).
// Re-type here so we can use it as a controlled input.
const NativeSelect = Select as unknown as React.FC<
  React.SelectHTMLAttributes<HTMLSelectElement> & { fullWidth?: boolean }
>

const EditPanelExpand = withCitrusTheme(Box, () => ({
  overflow: 'hidden',
  paddingTop: '24px',
  animation: 'editDeliveryDayExpand 220ms ease-out',
  '@keyframes editDeliveryDayExpand': {
    from: { maxHeight: 0, opacity: 0, paddingTop: 0 },
    to: { maxHeight: '320px', opacity: 1, paddingTop: '24px' },
  },
}))

const EditPanelCollapse = withCitrusTheme(Box, () => ({
  overflow: 'hidden',
  animation: 'editDeliveryDayCollapse 220ms ease-in forwards',
  '@keyframes editDeliveryDayCollapse': {
    from: { maxHeight: '320px', opacity: 1, paddingTop: '24px' },
    to: { maxHeight: 0, opacity: 0, paddingTop: 0 },
  },
}))

export type EditDeliveryDayRowProps = {
  currentDayLabel: string
  onConfirmComplete?: () => void
}

const BASKET_CLEAR_WARNING = 'Picking a new delivery day will empty your basket'

const TopLine = ({
  currentDayLabel,
  trailing,
}: {
  currentDayLabel: string
  trailing?: React.ReactNode
}) => (
  <Box
    display={Display.Flex}
    alignItems={AlignItems.Center}
    justifyContent={JustifyContent.SpaceBetween}
    gap={3}
  >
    <Box
      display={Display.Flex}
      flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
      alignItems={[AlignItems.FlexStart, null, AlignItems.Baseline]}
      gap={[1, null, 4]}
      flexGrow={1}
    >
      <Box width={['auto', null, '168px']} flexShrink={0}>
        <Body1 size={[2, null, 6]} color={Color.ColdGrey_800}>
          Earliest delivery
        </Body1>
      </Box>
      <Body1 size={[2, null, 6]} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
        {currentDayLabel}
      </Body1>
    </Box>
    {trailing}
  </Box>
)

export const EditDeliveryDayRow = ({
  currentDayLabel,
  onConfirmComplete,
}: EditDeliveryDayRowProps) => {
  const {
    availableSlots,
    currentOptionId,
    selectedOptionId,
    setSelectedOptionId,
    basketWillBeEmptied,
    canSave,
    confirm,
  } = useEditDeliveryDay()
  const [isEditing, setIsEditing] = useState(false)
  // isClosing keeps the picker block mounted through its collapse animation
  // before unmounting (mirrors the parent dropdown's open/close pattern).
  const [isClosing, setIsClosing] = useState(false)

  if (availableSlots.length === 0) {
    return <TopLine currentDayLabel={currentDayLabel} />
  }

  const startClose = () => {
    setIsEditing(false)
    setIsClosing(true)
  }

  const startOpen = () => {
    setIsClosing(false) // cancel any in-flight close animation
    setIsEditing(true)
  }

  if (!isEditing && !isClosing) {
    return (
      <TopLine
        currentDayLabel={currentDayLabel}
        trailing={
          <Button
            colorVariant={ButtonColorVariant.Tertiary}
            onClick={startOpen}
            aria-label="Edit delivery day"
          >
            Edit
          </Button>
        }
      />
    )
  }

  const handleSave = () => {
    confirm()
    startClose()
    onConfirmComplete?.()
  }

  const handleCancel = () => {
    setSelectedOptionId(currentOptionId)
    startClose()
  }

  // When isClosing, the TopLine button has already flipped back to "Edit" and
  // the panel below plays its collapse animation before unmounting.
  const trailing = isClosing ? (
    <Button
      colorVariant={ButtonColorVariant.Tertiary}
      onClick={startOpen}
      aria-label="Edit delivery day"
    >
      Edit
    </Button>
  ) : (
    <Button
      colorVariant={ButtonColorVariant.Tertiary}
      onClick={handleCancel}
      aria-label="Cancel delivery day edit"
    >
      Cancel
    </Button>
  )

  const PanelWrapper = isClosing ? EditPanelCollapse : EditPanelExpand
  const handleAnimationEnd = isClosing ? () => setIsClosing(false) : undefined

  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
      <TopLine currentDayLabel={currentDayLabel} trailing={trailing} />
      <PanelWrapper onAnimationEnd={handleAnimationEnd}>
        <Box
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
          gap={[1, null, 4]}
        >
          {/* Box is a spacer on desktop only - aligns the picker block with the bold text */}
          <Box
            width={['0', null, '168px']}
            flexShrink={0}
            display={[Display.None, null, Display.Block]}
          />
          <Box
            flexGrow={1}
            maxWidth="560px"
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={2}
          >
            <NativeSelect
              fullWidth
              value={selectedOptionId}
              onChange={(e) => setSelectedOptionId(e.currentTarget.value)}
              aria-label="Choose delivery day"
            >
              {availableSlots.map((option) => (
                <option key={option.optionId} value={option.optionId}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
            {basketWillBeEmptied && (
              <Body1 size={[1, null, 4]} color={Color.Error_800}>
                {BASKET_CLEAR_WARNING}
              </Body1>
            )}
            <Button
              colorVariant={ButtonColorVariant.Primary}
              disabled={!canSave}
              onClick={handleSave}
              aria-label="Save delivery day"
            >
              Save delivery day
            </Button>
          </Box>
        </Box>
      </PanelWrapper>
    </Box>
  )
}
